import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import LoadingSpinner from '../LoadingSpinner';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import { format, parseISO, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

const DNControlDataStructure = ({
  selectedProject,
  fetchWithAuth,
  products
}) => {

  // fake startDate and endDate from 2 years ago to now
  const startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 2));
  const endDate = new Date();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    measures: [],
    aggregatedMeasures: [],
    aggregatedMeasuresShopSign: []
  });
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const selectStyles = useSelectStyles();

  useEffect(() => {
    const fetchAllData = async () => {
      if (!selectedProject?.value || !selectedProduct?.value) return;

      setLoading(true);
      setError(null);

      const projectId = selectedProject.value;
      const formattedStartDate = new Date(startDate).toISOString().split('T')[0];
      const formattedEndDate = new Date(endDate).toISOString().split('T')[0];

      try {
        // Construire l'URL de base avec les paramètres communs
        const baseUrl = `${API_BASE_URL}/api/dwh/dn/${projectId}`;
        const baseParams = `startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
        const productsParams = `products[]=${encodeURIComponent(selectedProduct.value)}`;

        // Appeler les 3 endpoints en parallèle
        const [measuresRes, aggregatedRes, aggregatedShopSignRes] = await Promise.all([
          fetchWithAuth(`${baseUrl}/products/measures?${baseParams}&${productsParams}`),
          fetchWithAuth(`${baseUrl}/products/aggregated-measures?${baseParams}&${productsParams}`),
          fetchWithAuth(`${baseUrl}/products/aggregated-measures-shop-sign?${baseParams}&${productsParams}`)
        ]);

        // Vérifier et récupérer les données de chaque endpoint
        const [measuresData, aggregatedData, aggregatedShopSignData] = await Promise.all([
          measuresRes.ok ? measuresRes.json() : [],
          aggregatedRes.ok ? aggregatedRes.json() : [],
          aggregatedShopSignRes.ok ? aggregatedShopSignRes.json() : []
        ]);

        setData({
          measures: measuresData,
          aggregatedMeasures: aggregatedData,
          aggregatedMeasuresShopSign: aggregatedShopSignData
        });
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Une erreur est survenue lors de la récupération des données');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [selectedProject, selectedProduct, fetchWithAuth]);

  const processRawData = (rawData) => {
    if (!rawData || !rawData.length) return [];

    // Map pour stocker les données par semaine
    const weeklyData = new Map();
    // Set pour suivre tous les magasins connus jusqu'à présent
    const allKnownStores = new Set();
    // Map pour stocker le dernier état connu de chaque magasin (cumulatif)
    const storeLatestStatus = new Map();

    // Trier les données par date croissante pour assurer le bon ordre chronologique
    const sortedData = [...rawData].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Premier passage: collecter et organiser les données par semaine
    sortedData.forEach(record => {
      // Extraire l'année et la semaine de la date
      const date = new Date(record.date);
      const year = date.getFullYear();

      // Calculer le numéro de semaine
      const startOfYear = new Date(year, 0, 1);
      const days = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
      const week = Math.ceil((days + startOfYear.getDay() + 1) / 7);

      // Créer une clé unique pour la semaine
      const weekKey = `${year}-${week.toString().padStart(2, '0')}`;
      const storeId = record.interventionPoint;

      // Initialiser la semaine si elle n'existe pas
      if (!weeklyData.has(weekKey)) {
        weeklyData.set(weekKey, {
          WEEK: week,
          YEAR: year,
          DATE: date.toISOString().split('T')[0],
          visitedStoresThisWeek: new Map(), // Pour stocker la dernière visite de chaque magasin cette semaine
        });
      }

      const weekData = weeklyData.get(weekKey);
      allKnownStores.add(storeId);

      // Si c'est la première visite de ce magasin cette semaine ou si cette visite est plus récente
      if (!weekData.visitedStoresThisWeek.has(storeId) ||
          date > weekData.visitedStoresThisWeek.get(storeId).date) {
        weekData.visitedStoresThisWeek.set(storeId, {
          date: date,
          isPresent: record.isPresent,
          reasonCategory: record.reasonCategory
        });
      }
    });

    // Calculer les métriques pour chaque semaine, avec un état cumulatif
    const processedData = [];
    const sortedWeeks = Array.from(weeklyData.keys()).sort();

    sortedWeeks.forEach(weekKey => {
      const weekData = weeklyData.get(weekKey);

      // Mettre à jour l'état global des magasins avec les visites de cette semaine
      weekData.visitedStoresThisWeek.forEach((visit, storeId) => {
        storeLatestStatus.set(storeId, {
          isPresent: visit.isPresent,
          reasonCategory: visit.reasonCategory
        });
      });

      // Calculer les métriques en utilisant tous les magasins connus jusqu'à présent
      let presentCount = 0;
      let notPresentCount = 0;
      let noRefCount = 0;

      storeLatestStatus.forEach((status, storeId) => {
        if (status.reasonCategory === 'Not in Store Range') {
          noRefCount++;
        } else if (status.isPresent) {
          presentCount++;
        } else {
          notPresentCount++;
        }
      });

      processedData.push({
        WEEK: weekData.WEEK,
        YEAR: weekData.YEAR,
        DATE: weekData.DATE,
        PRESENT_SHOPS: presentCount,
        NOT_PRESENT_SHOPS: notPresentCount,
        NO_REF_SHOPS: noRefCount
      });
    });

    return processedData;
  };

  const prepareWeeklyData = () => {
    if (!data.aggregatedMeasures.length && !data.aggregatedMeasuresShopSign.length && !data.measures.length) {
      return [];
    }

    // Traiter les données brutes
    const rawProcessedData = processRawData(data.measures);

    // Créer un Map de toutes les semaines uniques
    const weekMap = new Map();

    // Ajouter les semaines des données agrégées
    data.aggregatedMeasures.forEach(d => {
      const weekKey = `${d.YEAR}${d.WEEK.toString().padStart(2, '0')}`;

      weekMap.set(weekKey, {
        weekKey,
        sortKey: parseInt(`${d.YEAR}${d.WEEK.toString().padStart(2, '0')}`),
        weekNumber: d.WEEK,
        year: d.YEAR,
        weekFormatted: `S${d.WEEK} ${d.YEAR}`,
        aggregated: {
          PRESENT_SHOPS: d.PRESENT_SHOPS || 0,
          NOT_PRESENT_SHOPS: d.NOT_PRESENT_SHOPS || 0,
          NO_REF_SHOPS: d.NO_REF_SHOPS || 0
        },
        raw: {
          PRESENT_SHOPS: 0,
          NOT_PRESENT_SHOPS: 0,
          NO_REF_SHOPS: 0
        },
        shopSign: {
          PRESENT_SHOPS: 0,
          NOT_PRESENT_SHOPS: 0,
          NO_REF_SHOPS: 0
        }
      });
    });

    // Ajouter les données brutes traitées
    rawProcessedData.forEach(d => {
      const weekKey = `${d.YEAR}${d.WEEK.toString().padStart(2, '0')}`;

      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, {
          weekKey,
          sortKey: parseInt(`${d.YEAR}${d.WEEK.toString().padStart(2, '0')}`),
          weekNumber: d.WEEK,
          year: d.YEAR,
          weekFormatted: `S${d.WEEK} ${d.YEAR}`,
          aggregated: {
            PRESENT_SHOPS: 0,
            NOT_PRESENT_SHOPS: 0,
            NO_REF_SHOPS: 0
          },
          raw: {
            PRESENT_SHOPS: 0,
            NOT_PRESENT_SHOPS: 0,
            NO_REF_SHOPS: 0
          },
          shopSign: {
            PRESENT_SHOPS: 0,
            NOT_PRESENT_SHOPS: 0,
            NO_REF_SHOPS: 0
          }
        });
      }

      const weekData = weekMap.get(weekKey);
      weekData.raw = {
        PRESENT_SHOPS: d.PRESENT_SHOPS || 0,
        NOT_PRESENT_SHOPS: d.NOT_PRESENT_SHOPS || 0,
        NO_REF_SHOPS: d.NO_REF_SHOPS || 0
      };
    });

    // Ajouter les données par enseigne
    data.aggregatedMeasuresShopSign.forEach(d => {
      const weekKey = `${d.YEAR}${d.WEEK.toString().padStart(2, '0')}`;

      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, {
          weekKey,
          sortKey: parseInt(`${d.YEAR}${d.WEEK.toString().padStart(2, '0')}`),
          weekNumber: d.WEEK,
          year: d.YEAR,
          weekFormatted: `S${d.WEEK} ${d.YEAR}`,
          aggregated: {
            PRESENT_SHOPS: 0,
            NOT_PRESENT_SHOPS: 0,
            NO_REF_SHOPS: 0
          },
          raw: {
            PRESENT_SHOPS: 0,
            NOT_PRESENT_SHOPS: 0,
            NO_REF_SHOPS: 0
          },
          shopSign: {
            PRESENT_SHOPS: 0,
            NOT_PRESENT_SHOPS: 0,
            NO_REF_SHOPS: 0
          }
        });
      }

      const weekData = weekMap.get(weekKey);
      weekData.shopSign.PRESENT_SHOPS += (d.PRESENT_SHOPS || 0);
      weekData.shopSign.NOT_PRESENT_SHOPS += (d.NOT_PRESENT_SHOPS || 0);
      weekData.shopSign.NO_REF_SHOPS += (d.NO_REF_SHOPS || 0);
    });

    return Array.from(weekMap.values())
      .sort((a, b) => a.sortKey - b.sortKey);
  };

  if (!selectedProject) {
    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 rounded-lg">
        Veuillez sélectionner un produit
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
        {error}
      </div>
    );
  }

  // Formater les dates pour l'affichage
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold dark:text-white mb-4">
        Contrôle des structures de données
      </h2>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Sélectionner un produit à analyser
        </label>
        <Select
          className="react-select-container"
          classNamePrefix="react-select"
          options={products}
          value={selectedProduct}
          onChange={setSelectedProduct}
          placeholder="Sélectionner un produit..."
          isClearable={true}
          styles={selectStyles}
          noOptionsMessage={() => "Aucun produit trouvé"}
        />
      </div>

      {selectedProduct ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow text-center">
              <h3 className="text-lg font-medium mb-4 dark:text-gray-200">
                Mesures brutes
              </h3>
              <p className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                {data.measures.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                enregistrements
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow text-center">
              <h3 className="text-lg font-medium mb-4 dark:text-gray-200">
                Mesures agrégées
              </h3>
              <p className="text-4xl font-bold text-green-600 dark:text-green-400 mb-2">
                {data.aggregatedMeasures.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                enregistrements
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow text-center">
              <h3 className="text-lg font-medium mb-4 dark:text-gray-200">
                Agrégées par enseigne
              </h3>
              <p className="text-4xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                {data.aggregatedMeasuresShopSign.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                enregistrements
              </p>
            </div>
          </div>

          <div className="mt-8 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Semaine
                  </th>
                  <th scope="col" colSpan="4" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Mesures brutes
                  </th>
                  <th scope="col" colSpan="4" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Mesures agrégées
                  </th>
                  <th scope="col" colSpan="4" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Mesures agrégées par enseigne
                  </th>
                </tr>
                <tr>
                  <th scope="col" className="px-6 py-3"></th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    Présent
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    Non présent
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    Sans réf.
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    DN (%)
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    Présent
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    Non présent
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    Sans réf.
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    DN (%)
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    Présent
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    Non présent
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    Sans réf.
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                    DN (%)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {prepareWeeklyData().map((weekData, index) => {
                  // Calculer les taux de DN pour chaque type de mesure
                  const calculateDN = (present, absent) => {
                    const total = present + absent;
                    return total > 0 ? ((present / total) * 100).toFixed(1) : "N/A";
                  };

                  const rawDN = calculateDN(
                    weekData.raw.PRESENT_SHOPS,
                    weekData.raw.NOT_PRESENT_SHOPS
                  );

                  const aggregatedDN = calculateDN(
                    weekData.aggregated.PRESENT_SHOPS,
                    weekData.aggregated.NOT_PRESENT_SHOPS
                  );

                  const shopSignDN = calculateDN(
                    weekData.shopSign.PRESENT_SHOPS,
                    weekData.shopSign.NOT_PRESENT_SHOPS
                  );

                  return (
                    <tr key={weekData.weekKey} className={index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {weekData.weekFormatted}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-300">
                        {weekData.raw.PRESENT_SHOPS}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-300">
                        {weekData.raw.NOT_PRESENT_SHOPS}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-300">
                        {weekData.raw.NO_REF_SHOPS}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center font-bold text-blue-600 dark:text-blue-400">
                        {rawDN}%
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-300">
                        {weekData.aggregated.PRESENT_SHOPS}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-300">
                        {weekData.aggregated.NOT_PRESENT_SHOPS}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-300">
                        {weekData.aggregated.NO_REF_SHOPS}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center font-bold text-green-600 dark:text-green-400">
                        {aggregatedDN}%
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-300">
                        {weekData.shopSign.PRESENT_SHOPS}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-300">
                        {weekData.shopSign.NOT_PRESENT_SHOPS}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-300">
                        {weekData.shopSign.NO_REF_SHOPS}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-center font-bold text-purple-600 dark:text-purple-400">
                        {shopSignDN}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">
          Veuillez sélectionner un produit pour voir les résultats
        </div>
      )}
    </div>
  );
};

export default DNControlDataStructure;
